import { useAutoPopulation as useBaseAutoPopulation } from '@shared/utils/autoPopulation/useAutoPopulation';
import { useStayAreasAutoPopulationApi } from './api/useStayAreasAutoPopulationApi';
import { useStayAreasAutoPopulationPolling } from './hooks/useStayAreasAutoPopulationPolling';
import type { StartAutoPopulationParams, UseAutoPopulationOptions, UseAutoPopulationReturn, AutoPopulationResult, StayAreasContext } from './stayAreasAutoPopulation.types';
import type { LatLngLiteral } from '@shared/core/google-maps/types';
import { getValidCoordinates } from '@shared/core/places/location';
import type { Maybe } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';

/**
 * API payload type for stay areas auto-population.
 */
interface ApiPayload
  extends Readonly<{
    eventId: string;
    location?: Maybe<LatLngLiteral>;
    context: StayAreasContext;
    scenario?: string;
  }> {}

/**
 * Unified hook for auto-population of stay areas.
 *
 * This hook automatically populates stay areas near a given location by calling the appropriate API.
 * It wraps the base auto-population hook with stay areas-specific configuration.
 *
 * IMPORTANT: All hooks are called unconditionally at the top level to follow React's Rules of Hooks.
 * This ensures stable hook call order across renders.
 *
 * @returns Hook return value containing start function, status, results, and error
 */
export const useStayAreasAutoPopulation = (options: UseAutoPopulationOptions): UseAutoPopulationReturn => {
  const { mode, onComplete, context } = options;

  // Get eventId for polling - called unconditionally at top level
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  // Call API hook unconditionally at top level
  const { callAutoPopulation, loading: apiLoading } = useStayAreasAutoPopulationApi();

  // Determine if polling should be skipped (start-only mode or no eventId)
  const shouldSkipAutoPopulationCheck = mode === 'start-only' || !eventId;

  // Call polling hook unconditionally at top level
  const pollingData = useStayAreasAutoPopulationPolling({
    eventId,
    shouldSkipAutoPopulationCheck,
    context,
  });

  return useBaseAutoPopulation<AutoPopulationResult, ApiPayload, StartAutoPopulationParams, StayAreasContext>({
    mode,
    onComplete,
    context,
    api: {
      callApi: callAutoPopulation,
      loading: apiLoading,
    },
    polling: pollingData,
    mapApiParams: (params, targetEventId, ctx) => ({
      eventId: targetEventId,
      location: getValidCoordinates(params.venue),
      context: ctx,
      scenario: params.scenario,
    }),
  });
};
